import { type ModalAction } from '@solinkcorp/solink-components';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@solinkcorp/solink-ui';
import { type ReactNode } from 'react';

interface SpotCheckPanelDialogProps {
  open: boolean;
  title: ReactNode;
  footer: ReactNode;
  actions?: ModalAction[];
  onClose: () => void;
  children: ReactNode;
}

/**
 * Full-width Spot Check overlay — same shell as dashboard widget settings.
 */
export default function SpotCheckPanelDialog({
  open,
  title,
  footer,
  actions,
  onClose,
  children,
}: SpotCheckPanelDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        data-testid="spot-check-panel-aspen"
        size="l"
        className={cn(
          'tw flex h-[90vh] max-h-[90vh] w-[90vw] max-w-[90vw] flex-col gap-0 bg-background-surface sm:max-w-[90vw]',
        )}
      >
        <DialogHeader className="tw mb-0 shrink-0 text-left">
          <div className="tw flex min-w-0 flex-wrap items-center gap-2">{title}</div>
          <DialogTitle className="tw sr-only">Spot Check</DialogTitle>
          <DialogDescription className="tw sr-only">Spot Check</DialogDescription>
        </DialogHeader>
        <div className="tw -mx-6 flex min-h-0 flex-1 flex-col overflow-hidden bg-background-muted px-6">
          {children}
        </div>
        <DialogFooter className="tw mt-0 shrink-0 flex-col gap-3 border-t border-border-muted sm:flex-row sm:items-center sm:justify-between">
          <div className="tw min-w-0 flex-1 overflow-visible text-left">{footer}</div>
          {actions && actions.length > 0 ? (
            <div className="tw flex shrink-0 flex-wrap justify-end gap-2">
              {actions.map((action) => (
                <Button
                  key={action.text ?? action.dataTestId ?? 'action'}
                  type="button"
                  variant={action.isPrimary === false ? 'outline' : 'default'}
                  disabled={action.disabled}
                  data-testid={action.dataTestId}
                  onClick={action.onClick}
                >
                  {action.text}
                </Button>
              ))}
            </div>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
